// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  Body,
  Button,
  Card,
  Column,
  Description,
  H3,
  Icon,
  LgPadding,
  Row,
  Separator,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import { isEmpty } from 'lodash';
import pluralize from 'pluralize';
import React, { useContext } from 'react';
import { selectPrescription } from '~shared/actions/prescriptions';
import { getImage } from '~shared/assets/images/getImage';
import { IMG_ALT_TEXTS } from '~shared/constants';
import { AUTO_REFILL_ACTION_CARD, AUTO_REFILL_EMPTY_LIST } from '~shared/features/auto-refill/constants';
import {
  getFormattedDeliveryDateForAutoRefillPrescriptions,
  getMedsWithAutoRefill,
} from '~shared/features/auto-refill/selectors/getMedsWithAutoRefill';
import { prescriptionIsActive } from '~shared/features/my-meds/helper';
import { formatQuantity } from '~shared/helpers/helper';
import { usePageViewedEvent } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type Prescription } from '~shared/types';
import { Breadcrumbs } from '~web/components/Breadcrumbs';
import { ContentWrapper, Main, PageBackground, Sidebar } from '~web/components/Page';
import { PageHeader } from '~web/components/PageHeader';
import { AutoRefillActionSheet } from '~web/features/auto-refill/components/AutoRefillActionSheet';
import { SidebarItem } from '~web/features/my-meds/components/SidebarItem';

const BREADCRUMBS = [
  {
    text: 'Account',
    to: '/me',
  },
  {
    text: 'Auto Refill',
  },
];

type PrescriptionProps = {
  readonly autoRefillDate: string;
  readonly medicationName: string;
  readonly prescription: Prescription;
};

const AutoRefillPrescription = ({ medicationName, autoRefillDate, prescription }: PrescriptionProps) => {
  const dispatch = useDispatchShared();
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const { id: prescriptionID, refills_remaining } = prescription;

  const openAutoRefillOffModal = () => {
    dispatch(selectPrescription(prescriptionID));
    setActiveActionSheet(<AutoRefillActionSheet />);
  };

  return (
    <>
      <LgPadding>
        <Row
          spaceBetween
          top
        >
          <Column gap="XXS">
            <H3>{medicationName}</H3>
            <Row
              centerVertically
              gap="XS"
            >
              <Icon
                name="sync-small"
                type="disabled"
              />
              <Description>
                {`${refills_remaining || 'No'} ${pluralize('Refill', refills_remaining)} Left (QTY: ${formatQuantity(
                  prescription,
                )})`}
              </Description>
            </Row>
            <Row
              centerVertically
              gap="XS"
            >
              <Icon
                name="calendar-small"
                type="disabled"
              />
              <Description>{`Auto refills on ${autoRefillDate}`}</Description>
            </Row>
          </Column>
          <Column>
            <Button
              label="Turn auto refill off"
              accessibilityLabel="Press to turn auto refill off"
              type="secondary"
              onPress={openAutoRefillOffModal}
              width="inline"
              small
            />
          </Column>
        </Row>
      </LgPadding>
      <Separator />
    </>
  );
};

export const ProfileAutoRefills = () => {
  const { navigate } = useNavigation();
  const medicationsWithAutoRefills = useSelectorShared(getMedsWithAutoRefill);
  const deliveryDatesByPrescriptionID = useSelectorShared(getFormattedDeliveryDateForAutoRefillPrescriptions);

  usePageViewedEvent({
    event: EVENTS.AUTO_REFILL_MANAGEMENT_CENTER_VIEWED,
  });

  const navigateToMedList = () => {
    navigate('RouteAppTabNavigator', { screen: 'RouteTabMyMeds' });
  };

  return (
    <PageBackground>
      <ContentWrapper>
        <Column>
          <Breadcrumbs breadcrumbs={BREADCRUMBS} />
          <PageHeader>Auto refill</PageHeader>
        </Column>
        <Main>
          <Card title="Medications on auto refill">
            {isEmpty(medicationsWithAutoRefills) ? (
              <LgPadding topPadding={SPACING.STATIC.NONE}>
                <Body>{AUTO_REFILL_EMPTY_LIST.body}</Body>
              </LgPadding>
            ) : (
              medicationsWithAutoRefills.reduce<React.ReactNode[]>((rxRows, { medicationName, prescriptions }) => {
                const rxWithAutoRefillRows = prescriptions.reduce<React.ReactNode[]>(
                  (rxsWithAutoRefills, prescription) => {
                    const { auto_refill, id } = prescription;

                    if (prescriptionIsActive(prescription) && auto_refill) {
                      rxsWithAutoRefills.push(
                        <AutoRefillPrescription
                          autoRefillDate={deliveryDatesByPrescriptionID[id]}
                          medicationName={medicationName}
                          prescription={prescription}
                          key={`auto-refill-prescription-${id}`}
                        />,
                      );
                    }

                    return rxsWithAutoRefills;
                  },
                  [],
                );

                return [...rxRows, ...rxWithAutoRefillRows];
              }, [])
            )}
          </Card>
        </Main>
        <Sidebar>
          <SidebarItem
            buttonText={AUTO_REFILL_ACTION_CARD.buttonText}
            img={getImage('autoRefill')}
            imgAlt={IMG_ALT_TEXTS.autoRefill}
            onClick={navigateToMedList}
            text={AUTO_REFILL_ACTION_CARD.body}
          />
        </Sidebar>
      </ContentWrapper>
    </PageBackground>
  );
};
